<script lang="ts" setup>
import { ref, computed, defineProps, defineEmits } from "vue";
import BaseButton from "@/components/base/BaseButton.vue";
import type { FeedbackFormType } from "@/struct/FeedbackStruct";
import { useStore } from "@/store";
import BaseIcon from "@/components/base/BaseIcon.vue";
import { useI18n } from "vue-i18n";

defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "submit-result", value: boolean): void;
}>();

//store logics
const store = useStore();
const { t } = useI18n();

const currentUserPhone = computed<number>(
  () => store.getters["tariff/currentUserPhone"]
);

const closeModal = () => {
  emit("update:modelValue", false);
};

//answers logics
type AnswerType = 1 | 2 | 3 | null;

interface Question {
  title: string;
  subtitle: string;
  answer: AnswerType;
}

const questions = ref<Question[]>([
  {
    title: t("base.feedback.questions.tariffTitle"),
    subtitle: t("base.feedback.questions.tariffSubtitle"),
    answer: null,
  },
  {
    title: t("base.feedback.questions.siteTitle"),
    subtitle: t("base.feedback.questions.siteSubtitle"),
    answer: null,
  },
  {
    title: t("base.feedback.questions.supportTitle"),
    subtitle: t("base.feedback.questions.supportSubtitle"),
    answer: null,
  },
]);

const pickAnswer = (answer: Exclude<AnswerType, null>, index: number) => {
  questions.value[index].answer = answer;
};

const isFormValid = computed(() => {
  const allAnswered = questions.value.every((q) => q.answer !== null);
  return allAnswered && comment.value.trim().length > 0;
});

//text area logics
const comment = ref("");
const MAX_LENGTH = 260;

const symbolsCount = computed(() => comment.value.length);

const submit = async () => {
  if (!isFormValid.value) return;

  const data: FeedbackFormType = {
    msisdn: currentUserPhone.value,
    rate_tariff: questions.value[0].answer as number,
    rate_service: questions.value[1].answer as number,
    rate_website: questions.value[2].answer as number,
    comment: comment.value.trim(),
  };
  try {
    const res = await store.dispatch("feedback/sendFeedback", data);
    console.log(res);
    emit("submit-result", true);
  } catch (err) {
    console.log(err);
    emit("submit-result", false);
  } finally {
    closeModal();
  }
};
</script>

<template>
  <div v-if="modelValue" class="feedback-modal">
    <div class="feedback-modal--window">
      <div class="feedback-modal--close color-secondary" @click="closeModal">
        <BaseIcon name="close" />
      </div>
      <div class="feedback-modal--title">
        <p class="h3 color-secondary">{{ t("base.feedback.title") }}</p>
      </div>

      <div class="feedback-modal--wrapper">
        <div class="feedback-modal--questions">
          <div
            v-for="(item, index) in questions"
            :key="index"
            class="feedback-modal--questions-item"
          >
            <div class="feedback-modal--questions-title">
              <p class="h4 color-secondary">{{ item.title }}</p>
            </div>
            <div class="feedback-modal--questions-subtitle">
              <p class="caption-medium">
                {{ item.subtitle }}
              </p>
            </div>
            <div class="feedback-modal--questions-answers">
              <div class="feedback-modal--questions-answers-item">
                <div
                  class="feedback-modal--questions-answers-item_pic"
                  @click="pickAnswer(1, index)"
                  :class="{ active: item.answer === 1 }"
                >
                  <img src="@/assets/img/feedback/face-bad.png" alt="face" />
                </div>
                <p class="caption-medium">
                  {{ t("base.feedback.answers.bad") }}
                </p>
              </div>
              <div class="feedback-modal--questions-answers-item">
                <div
                  class="feedback-modal--questions-answers-item_pic"
                  @click="pickAnswer(2, index)"
                  :class="{ active: item.answer === 2 }"
                >
                  <img src="@/assets/img/feedback/face-normal.png" alt="face" />
                </div>
                <p class="caption-medium">
                  {{ t("base.feedback.answers.normal") }}
                </p>
              </div>
              <div class="feedback-modal--questions-answers-item">
                <div
                  class="feedback-modal--questions-answers-item_pic"
                  @click="pickAnswer(3, index)"
                  :class="{ active: item.answer === 3 }"
                >
                  <img src="@/assets/img/feedback/face-good.png" alt="face" />
                </div>
                <p class="caption-medium">
                  {{ t("base.feedback.answers.good") }}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="feedback-modal--comment">
          <div class="feedback-modal--comment-wrapper">
            <textarea
              v-model="comment"
              :maxlength="MAX_LENGTH"
              :placeholder="t('base.feedback.placeholder')"
              class="feedback-modal--comment-textarea"
            />
            <div class="feedback-modal--comment-counter">
              {{ symbolsCount }} / {{ MAX_LENGTH }}
            </div>
          </div>
        </div>
      </div>
      <div class="feedback-modal--btn">
        <BaseButton
          :title="t('base.feedback.submit')"
          :disabled="!isFormValid"
          @click="submit"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/colors";

.feedback-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  z-index: 1000;

  &--window {
    background: var(--color-white);
    border-radius: 32px;
    padding: clamp(20px, 3vw, 32px);
    display: flex;
    flex-direction: column;
    gap: clamp(20px, 2vw, 32px);
    position: relative;
    width: 100%;
    max-width: 520px;
    max-height: 90vh;
  }

  &--close {
    position: absolute;
    right: 22px;
    top: 22px;
    cursor: pointer;
  }

  &--wrapper {
    overflow-y: auto;
    flex: 1;
    padding-right: 4px;
  }

  &--btn {
    margin-top: auto;
  }

  &--questions {
    display: flex;
    flex-flow: column;
    gap: 24px;

    &-item {
      display: flex;
      flex-flow: column;
    }

    &-title {
      margin-bottom: 4px;
    }

    &-subtitle {
      margin-bottom: 24px;
    }

    &-answers {
      display: flex;
      flex-flow: row;
      align-items: center;
      justify-content: center;
      gap: 12px;

      &-item {
        display: flex;
        flex-flow: column;
        gap: 8px;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &_pic {
          background: var(--color-bg-primary);
          padding: 10px;
          border-radius: 30px;
          display: flex;
          align-items: center;
          justify-content: center;

          &.active {
            background: var(--color-secondary);
          }
        }
      }
    }
  }

  &--comment {
    margin-top: 32px;

    &-wrapper {
      position: relative;
      background: var(--color-bg-primary);
      border-radius: 24px;
      padding: 20px;
    }

    &-textarea {
      width: 100%;
      min-height: 120px;
      resize: none;
      border: none;
      outline: none;
      background: transparent;
      font-family: inherit;
      font-size: 16px;
      line-height: 1.4;
      color: var(--color-text-primary);

      &::placeholder {
        color: #8e8e93;
      }
    }

    &-counter {
      position: absolute;
      bottom: 12px;
      right: 16px;
      font-size: 14px;
      color: #8e8e93;
    }
  }
}

@media (max-width: 1024px) {
  .feedback-modal {
    padding: 0;
    align-items: flex-end;

    &--window {
      width: 100%;
      max-width: 100%;
      height: 100dvh;
      max-height: 100dvh;
      border-radius: 32px 32px 0 0;
      padding: 24px 20px 32px;
    }

    &--wrapper {
      height: auto;
      overflow-y: auto;
    }

    &--questions-answers {
      gap: 20px;

      &-item_pic {
        padding: 14px;
      }
    }

    &--comment-wrapper {
      border-radius: 20px;
    }
  }
}

.feedback-modal--questions-answers {
  flex-wrap: wrap;
}

@media (max-width: 480px) {
  .feedback-modal--questions-answers {
    justify-content: space-between;
  }

  .feedback-modal--questions-answers-item {
    flex: 1;
  }
}

@media (min-width: 1600px) {
  .feedback-modal--window {
    max-width: 600px;
  }
}
</style>
